import React, { useState, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Image as ImageIcon, 
  Upload, 
  Link as LinkIcon, 
  Loader2,
  Search,
  Check,
  Sparkles,
  FolderOpen,
  Plus,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useMediaLibrary } from '@/hooks/useMediaLibrary';
import { useGeneratedImages } from '@/hooks/useGeneratedImages';
import { useDocHubPhotos } from '@/hooks/useDocHubPhotos';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';

interface ArticleMediaLibraryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (url: string, alt?: string) => void;
}

export function ArticleMediaLibrary({ open, onOpenChange, onSelect }: ArticleMediaLibraryProps) {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState('');
  const [altText, setAltText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Hooks for different sources
  const { media: uploadedMedia, isLoading: isLoadingUploaded, uploadMedia, updateMedia, deleteMedia } = useMediaLibrary();
  const { images: generatedImages, isLoading: isLoadingGenerated } = useGeneratedImages();
  const { photos: docHubPhotos, isLoading: isLoadingDocHub } = useDocHubPhotos();

  const isUploading = uploadMedia.isPending;
  const isUpdating = updateMedia.isPending;
  const isDeleting = deleteMedia.isPending;

  const handleDeleteImage = useCallback(async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (confirm('Delete this image from the library?')) {
      await deleteMedia.mutateAsync(id);
      if (selectedImage && uploadedMedia.find(m => m.id === id)?.url === selectedImage) {
        setSelectedImage(null);
        setAltText('');
      }
    }
  }, [deleteMedia, selectedImage, uploadedMedia]);

  // Filter function
  const filterBySearch = (name: string) => 
    name.toLowerCase().includes(searchQuery.toLowerCase());

  const filteredUploaded = uploadedMedia.filter(item => filterBySearch(item.name));
  const filteredGenerated = generatedImages.filter(item => filterBySearch(item.id));
  const filteredDocHub = docHubPhotos.filter(item => filterBySearch(item.name));

  // Handle image selection with alt text pre-fill
  const handleSelectImage = (url: string, existingAltText?: string | null) => {
    setSelectedImage(url);
    setUrlInput('');
    // Always set alt text from selected image (empty if none exists)
    setAltText(existingAltText || '');
  };

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 5MB limit`);
        continue;
      }
      
      try {
        const result = await uploadMedia.mutateAsync({ file, altText: '' });
        setSelectedImage(result.url);
      } catch (error) {
        // Error handled by hook
      }
    }
    
    // Reset file input
    e.target.value = '';
  }, [uploadMedia]);

  const handleInsert = async () => {
    const imageUrl = selectedImage || urlInput;
    if (!imageUrl) {
      toast.error('Please select or enter an image URL');
      return;
    }
    
    // If a library image is selected and alt text was entered/modified, update the database
    if (selectedImage && altText) {
      const mediaItem = uploadedMedia.find(m => m.url === selectedImage);
      if (mediaItem && mediaItem.alt_text !== altText) {
        try {
          await updateMedia.mutateAsync({ id: mediaItem.id, altText });
        } catch (error) {
          // Continue with insertion even if update fails
          console.error('Failed to update alt text:', error);
        }
      }
    }
    
    onSelect(imageUrl, altText);
    onOpenChange(false);
    setSelectedImage(null);
    setUrlInput('');
    setAltText('');
  };

  const ImageGrid = ({ 
    images, 
    isLoading, 
    emptyText,
    onSelectImage,
    onDelete
  }: { 
    images: { id: string; url: string; name?: string; altText?: string | null }[]; 
    isLoading: boolean; 
    emptyText: string;
    onSelectImage: (url: string, altText?: string | null) => void;
    onDelete?: (e: React.MouseEvent, id: string) => void;
  }) => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      );
    }
    
    if (images.length === 0) {
      return (
        <p className="text-sm text-muted-foreground py-4 text-center">{emptyText}</p>
      );
    }

    return (
      <div className="grid grid-cols-5 gap-2">
        {images.map((item) => (
          <div
            key={item.id}
            onClick={() => onSelectImage(item.url, item.altText)}
            className={cn(
              "group relative aspect-square rounded-md overflow-hidden border-2 transition-all hover:opacity-90 cursor-pointer",
              selectedImage === item.url 
                ? "border-primary ring-2 ring-primary/20" 
                : "border-border hover:border-muted-foreground/40"
            )}
          >
            <img
              src={item.url}
              alt={item.altText || item.name || 'Image'}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            {selectedImage === item.url && (
              <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                <Check className="h-5 w-5 text-primary" />
              </div>
            )}
            {onDelete && (
              <button
                onClick={(e) => onDelete(e, item.id)}
                className="absolute top-1 right-1 p-1 rounded bg-destructive/90 text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive"
                disabled={isDeleting}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Media Library
          </DialogTitle>
        </DialogHeader>

        {/* Search Bar */}
        <div className="relative shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search images..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 min-h-0 overflow-y-auto space-y-6 pr-2">
          {/* Uploaded Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Upload className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Uploaded</Label>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                disabled={isUploading}
              />
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {isUploading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Plus className="h-4 w-4 mr-1" />
                )}
                Upload
              </Button>
            </div>
            <ImageGrid 
              images={filteredUploaded.map(m => ({ id: m.id, url: m.url, name: m.name, altText: m.alt_text }))} 
              isLoading={isLoadingUploaded} 
              emptyText="No uploaded images yet"
              onSelectImage={handleSelectImage}
              onDelete={handleDeleteImage}
            />
          </div>

          <Separator />

          {/* Generated Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Generated</Label>
            </div>
            <ImageGrid 
              images={filteredGenerated.map(g => ({ id: g.id, url: g.url }))} 
              isLoading={isLoadingGenerated} 
              emptyText="No generated images"
              onSelectImage={handleSelectImage}
            />
          </div>

          <Separator />

          {/* Doc Hub Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FolderOpen className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Doc Hub</Label>
            </div>
            <ImageGrid 
              images={filteredDocHub.map(p => ({ id: p.id, url: p.url, name: p.name }))} 
              isLoading={isLoadingDocHub} 
              emptyText="No Doc Hub photos"
              onSelectImage={handleSelectImage}
            />
          </div>

          <Separator />

          {/* From URL Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <LinkIcon className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">From URL</Label>
            </div>
            <Input
              placeholder="https://example.com/image.jpg"
              value={urlInput}
              onChange={(e) => {
                setUrlInput(e.target.value);
                setSelectedImage(null);
              }}
            />
            {urlInput && (
              <div className="mt-3 border rounded-lg p-3 bg-muted/30">
                <img 
                  src={urlInput} 
                  alt="Preview" 
                  className="max-h-[120px] mx-auto rounded"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="space-y-4 pt-4 border-t shrink-0">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Alt Text (optional)</Label>
            <Input
              placeholder="Describe the image for accessibility"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleInsert} disabled={(!selectedImage && !urlInput) || isUpdating}>
              {isUpdating && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Insert Image
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
